import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, CircularProgress, Alert,
  Breadcrumbs, Link, Paper, Grid, List, ListItem, ListItemButton,
  ListItemText, TextField, IconButton, Tooltip, Checkbox, Button, FormControlLabel,
  Select, MenuItem, FormControl, InputLabel, Chip, Divider, Card, CardContent,
  ButtonGroup, Pagination, Stack,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LinkIcon from '@mui/icons-material/Link';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DeleteIcon from '@mui/icons-material/Delete';
import VerifiedIcon from '@mui/icons-material/Verified';
import RefreshIcon from '@mui/icons-material/Refresh';
import { supabase } from './supabaseClient';

const UserFilesPage = () => {
  const { userId } = useParams();
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [loadingContent, setLoadingContent] = useState(false);
  const [error, setError] = useState(null);
  const [userEmail, setUserEmail] = useState('');

  // State for Chatwoot functionality (legacy)
  const [chatwootSelection, setChatwootSelection] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // State for Chatwoot integration management
  const [fileIntegrations, setFileIntegrations] = useState({}); // { fileId: [integrationId, ...] }
  const [stagedIntegrations, setStagedIntegrations] = useState({}); // Staged changes before save
  const [availableIntegrations, setAvailableIntegrations] = useState([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [savingIntegrations, setSavingIntegrations] = useState(false);

  // State for Widget integration management
  const [widgetFileIntegrations, setWidgetFileIntegrations] = useState({});
  const [stagedWidgetIntegrations, setStagedWidgetIntegrations] = useState({});
  const [availableWidgetIntegrations, setAvailableWidgetIntegrations] = useState([]);
  const [loadingWidgetIntegrations, setLoadingWidgetIntegrations] = useState(false);

  // State for Instagram integration management
  const [instagramFileIntegrations, setInstagramFileIntegrations] = useState({});
  const [stagedInstagramIntegrations, setStagedInstagramIntegrations] = useState({});
  const [availableInstagramChannels, setAvailableInstagramChannels] = useState([]);
  const [loadingInstagramChannels, setLoadingInstagramChannels] = useState(false);

  // Batch selection state
  const [selectedFileIds, setSelectedFileIds] = useState(new Set());
  const [availableTeams, setAvailableTeams] = useState([]);
  const [teamNames, setTeamNames] = useState({}); // Map team_id -> team_name

  // Pagination and sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState('date_desc'); // date_desc, date_asc, name_asc, name_desc, status

  // Filters
  const [filterTeam, setFilterTeam] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Deletion state
  const [deletingFileId, setDeletingFileId] = useState(null);

  // Validation and Force Recrawl state
  const [isValidating, setIsValidating] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [isForceRecrawling, setIsForceRecrawling] = useState(false);

  const getAuthToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  }, []);

  const fetchInitialData = useCallback(async () => {
    if (!userId) return;
    setLoadingFiles(true);
    setError(null);
    try {
      // Fetch user email via admin Edge Function (auth.users is not available via PostgREST)
      let email = userId;
      try {
        const token = await getAuthToken();
        const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY || '';
        const resp = await fetch(`/functions/v1/admin-user-manager/users/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'apikey': anonKey }
        });
        if (resp.ok) {
          const data = await resp.json();
          email = data?.user?.email || data?.email || email;
        } else {
          console.warn('Admin user fetch failed with status', resp.status);
        }
      } catch (e) {
        console.warn("Could not fetch user email via admin function, will use ID.", e);
      }
      setUserEmail(email);

      const { data: filesData, error: filesError } = await supabase
        .from('user_files')
        .select('id, file_name, created_at, status, processing_status, source_url, use_in_chatwoot, team_id')
        .eq('user_id', userId)
        .not('file_name', 'is', null) // Filter out orphaned files with NULL file_name
        .order('created_at', { ascending: false });

      if (filesError) throw filesError;
      setFiles(filesData || []);

      // Extract unique teams
      const teams = [...new Set((filesData || []).map(f => f.team_id).filter(Boolean))];
      setAvailableTeams(teams);

      // Fetch team names for display
      if (teams.length > 0) {
        const { data: teamsData, error: teamsError } = await supabase
          .from('teams')
          .select('team_id, name')
          .in('team_id', teams);

        if (!teamsError && teamsData) {
          const nameMap = {};
          teamsData.forEach(team => {
            nameMap[team.team_id] = team.name;
          });
          setTeamNames(nameMap);
        }
      }

      // Initialize chatwootSelection state
      const initialSelection = (filesData || []).reduce((acc, file) => {
        acc[file.id] = !!file.use_in_chatwoot;
        return acc;
      }, {});
      setChatwootSelection(initialSelection);

    } catch (err) {
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoadingFiles(false);
    }
  }, [userId, getAuthToken]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Load available Chatwoot integrations for this user
  useEffect(() => {
    const loadIntegrations = async () => {
      if (!userId) return;

      setLoadingIntegrations(true);
      try {
        const { data, error } = await supabase
          .rpc('get_user_chatwoot_integrations_summary', { p_user_id: userId });

        if (error) {
          console.error('Failed to load integrations:', error);
          return;
        }

        setAvailableIntegrations((data || []).filter(int => int.is_active));
      } catch (e) {
        console.error('Failed to load integrations:', e);
      } finally {
        setLoadingIntegrations(false);
      }
    };

    loadIntegrations();
  }, [userId]);

  // Load available Widget integrations for this user
  useEffect(() => {
    const loadWidgetIntegrations = async () => {
      if (!userId) return;

      setLoadingWidgetIntegrations(true);
      try {
        const { data, error } = await supabase
          .rpc('get_user_widget_integrations_summary', { p_user_id: userId });

        if (error) {
          console.error('Failed to load widget integrations:', error);
          return;
        }

        setAvailableWidgetIntegrations((data || []).filter(int => int.is_active));
      } catch (e) {
        console.error('Failed to load widget integrations:', e);
      } finally {
        setLoadingWidgetIntegrations(false);
      }
    };

    loadWidgetIntegrations();
  }, [userId]);

  // Load available Instagram channels for this user
  useEffect(() => {
    const loadInstagramChannels = async () => {
      if (!userId) return;

      setLoadingInstagramChannels(true);
      try {
        const { data, error } = await supabase
          .rpc('get_user_instagram_channels_summary', { p_user_id: userId });

        if (error) {
          console.error('Failed to load Instagram channels:', error);
          return;
        }

        setAvailableInstagramChannels((data || []).filter(ch => ch.is_active));
      } catch (e) {
        console.error('Failed to load Instagram channels:', e);
      } finally {
        setLoadingInstagramChannels(false);
      }
    };

    loadInstagramChannels();
  }, [userId]);

  // Load existing Chatwoot file-integration mappings
  useEffect(() => {
    const loadFileMappings = async () => {
      if (files.length === 0 || availableIntegrations.length === 0) return;

      try {
        const mappings = {};

        // Load mappings for all files
        for (const file of files) {
          const { data, error } = await supabase
            .rpc('get_integrations_for_file', { p_file_id: file.id });

          if (error) {
            console.error(`Failed to load mappings for file ${file.id}:`, error);
            mappings[file.id] = [];
          } else {
            mappings[file.id] = (data || []).map(int => int.integration_id);
          }
        }

        setFileIntegrations(mappings);
        setStagedIntegrations(mappings); // Initialize staged with current
      } catch (e) {
        console.error('Failed to load file mappings:', e);
      }
    };

    if (files.length > 0 && availableIntegrations.length > 0) {
      loadFileMappings();
    }
  }, [files, availableIntegrations]);

  // Load existing Widget file-integration mappings
  useEffect(() => {
    const loadWidgetFileMappings = async () => {
      if (files.length === 0 || availableWidgetIntegrations.length === 0) return;

      try {
        const mappings = {};

        for (const file of files) {
          const { data, error } = await supabase
            .rpc('get_widget_integrations_for_file', { p_file_id: file.id });

          if (error) {
            console.error(`Failed to load widget mappings for file ${file.id}:`, error);
            mappings[file.id] = [];
          } else {
            mappings[file.id] = (data || []).map(int => int.integration_id);
          }
        }

        setWidgetFileIntegrations(mappings);
        setStagedWidgetIntegrations(mappings);
      } catch (e) {
        console.error('Failed to load widget file mappings:', e);
      }
    };

    if (files.length > 0 && availableWidgetIntegrations.length > 0) {
      loadWidgetFileMappings();
    }
  }, [files, availableWidgetIntegrations]);

  // Load existing Instagram file-integration mappings
  useEffect(() => {
    const loadInstagramFileMappings = async () => {
      if (files.length === 0 || availableInstagramChannels.length === 0) return;

      try {
        const mappings = {};

        for (const file of files) {
          const { data, error } = await supabase
            .rpc('get_instagram_integrations_for_file', { p_file_id: file.id });

          if (error) {
            console.error(`Failed to load Instagram mappings for file ${file.id}:`, error);
            mappings[file.id] = [];
          } else {
            mappings[file.id] = (data || []).map(int => int.integration_id);
          }
        }

        setInstagramFileIntegrations(mappings);
        setStagedInstagramIntegrations(mappings);
      } catch (e) {
        console.error('Failed to load Instagram file mappings:', e);
      }
    };

    if (files.length > 0 && availableInstagramChannels.length > 0) {
      loadInstagramFileMappings();
    }
  }, [files, availableInstagramChannels]);

  // Filtered, sorted, and paginated files
  const filteredAndSortedFiles = useMemo(() => {
    let filtered = [...files];

    // Apply team filter
    if (filterTeam) {
      filtered = filtered.filter(f => f.team_id === filterTeam);
    }

    // Apply status filter
    if (filterStatus) {
      filtered = filtered.filter(f => (f.processing_status || f.status) === filterStatus);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'date_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'name_asc':
          return (a.file_name || '').localeCompare(b.file_name || '');
        case 'name_desc':
          return (b.file_name || '').localeCompare(a.file_name || '');
        case 'status':
          return (a.processing_status || a.status || '').localeCompare(b.processing_status || b.status || '');
        default:
          return 0;
      }
    });

    return filtered;
  }, [files, filterTeam, filterStatus, sortBy]);

  // Paginated files
  const paginatedFiles = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedFiles.slice(startIndex, endIndex);
  }, [filteredAndSortedFiles, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedFiles.length / itemsPerPage);

  const handleFileSelect = async (file) => {
    setSelectedFile(file);
    setLoadingContent(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('user_files')
        .select('extracted_full_text, searchable_summary, doc_summary')
        .eq('id', file.id)
        .single();

      if (error) throw error;

      setSelectedFile(prev => ({
        ...prev,
        content: data?.extracted_full_text || 'No extracted text content available for this file.',
        searchable_summary: data?.searchable_summary,
        summary: data?.doc_summary
      }));
    } catch (err) {
      setError(`Failed to load file content: ${err.message}`);
      setSelectedFile(prev => ({ ...prev, content: 'Error loading content.' }));
    } finally {
      setLoadingContent(false);
    }
  };

  const handleChatwootCheckboxChange = (fileId) => {
    setChatwootSelection(prev => ({
      ...prev,
      [fileId]: !prev[fileId],
    }));
  };

  const handleSelectAllChatwoot = (event) => {
    const isChecked = event.target.checked;
    const newSelection = {};
    files.forEach(file => {
      newSelection[file.id] = isChecked;
    });
    setChatwootSelection(newSelection);
  };

  // Batch selection handlers
  const handleSelectAll = () => {
    const allIds = new Set(filteredAndSortedFiles.map(f => f.id));
    setSelectedFileIds(allIds);
  };

  const handleSelectByTeam = (teamId) => {
    if (!teamId) return;
    const teamFileIds = new Set(
      filteredAndSortedFiles.filter(f => f.team_id === teamId).map(f => f.id)
    );
    setSelectedFileIds(teamFileIds);
  };

  const handleSelectByStatus = (status) => {
    if (!status) return;
    const statusFileIds = new Set(
      filteredAndSortedFiles.filter(f => (f.processing_status || f.status) === status).map(f => f.id)
    );
    setSelectedFileIds(statusFileIds);
  };

  const handleClearSelection = () => {
    setSelectedFileIds(new Set());
  };

  const handleToggleFileSelection = (fileId) => {
    setSelectedFileIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileId)) {
        newSet.delete(fileId);
      } else {
        newSet.add(fileId);
      }
      return newSet;
    });
  };

  // Batch integration handlers (staged)
  const handleBatchApplyIntegration = (integrationId) => {
    if (!integrationId || selectedFileIds.size === 0) return;

    setStagedIntegrations(prev => {
      const updated = { ...prev };
      selectedFileIds.forEach(fileId => {
        const current = updated[fileId] || [];
        if (!current.includes(integrationId)) {
          updated[fileId] = [...current, integrationId];
        }
      });
      return updated;
    });
  };

  const handleBatchRemoveIntegration = (integrationId) => {
    if (!integrationId || selectedFileIds.size === 0) return;

    setStagedIntegrations(prev => {
      const updated = { ...prev };
      selectedFileIds.forEach(fileId => {
        const current = updated[fileId] || [];
        updated[fileId] = current.filter(id => id !== integrationId);
      });
      return updated;
    });
  };

  // Individual file integration change (staged)
  const handleIndividualIntegrationChange = (fileId, integrationIds) => {
    setStagedIntegrations(prev => ({
      ...prev,
      [fileId]: integrationIds
    }));
  };

  // Widget batch handlers
  const handleBatchApplyWidgetIntegration = (widgetId) => {
    if (!widgetId || selectedFileIds.size === 0) return;

    setStagedWidgetIntegrations(prev => {
      const updated = { ...prev };
      selectedFileIds.forEach(fileId => {
        const current = updated[fileId] || [];
        if (!current.includes(widgetId)) {
          updated[fileId] = [...current, widgetId];
        }
      });
      return updated;
    });
  };

  const handleBatchRemoveWidgetIntegration = (widgetId) => {
    if (!widgetId || selectedFileIds.size === 0) return;

    setStagedWidgetIntegrations(prev => {
      const updated = { ...prev };
      selectedFileIds.forEach(fileId => {
        const current = updated[fileId] || [];
        updated[fileId] = current.filter(id => id !== widgetId);
      });
      return updated;
    });
  };

  const handleIndividualWidgetIntegrationChange = (fileId, widgetIds) => {
    setStagedWidgetIntegrations(prev => ({
      ...prev,
      [fileId]: widgetIds
    }));
  };

  // Instagram batch handlers
  const handleBatchApplyInstagramIntegration = (channelId) => {
    console.log('=== handleBatchApplyInstagramIntegration ===');
    console.log('channelId:', channelId, 'type:', typeof channelId);
    console.log('selectedFileIds:', [...selectedFileIds]);
    console.log('selectedFileIds.size:', selectedFileIds.size);

    if (!channelId || selectedFileIds.size === 0) {
      console.log('Returning early - no channelId or no files selected');
      return;
    }

    setStagedInstagramIntegrations(prev => {
      console.log('Previous stagedInstagramIntegrations:', prev);
      const updated = { ...prev };
      selectedFileIds.forEach(fileId => {
        const current = updated[fileId] || [];
        console.log(`File ${fileId}: current integrations:`, current);
        if (!current.includes(channelId)) {
          updated[fileId] = [...current, channelId];
          console.log(`File ${fileId}: added channelId, now:`, updated[fileId]);
        }
      });
      console.log('Updated stagedInstagramIntegrations:', updated);
      return updated;
    });
  };

  const handleBatchRemoveInstagramIntegration = (channelId) => {
    if (!channelId || selectedFileIds.size === 0) return;

    setStagedInstagramIntegrations(prev => {
      const updated = { ...prev };
      selectedFileIds.forEach(fileId => {
        const current = updated[fileId] || [];
        updated[fileId] = current.filter(id => id !== channelId);
      });
      return updated;
    });
  };

  const handleIndividualInstagramIntegrationChange = (fileId, channelIds) => {
    setStagedInstagramIntegrations(prev => ({
      ...prev,
      [fileId]: channelIds
    }));
  };

  // Check if there are unsaved changes (Chatwoot OR Widget OR Instagram)
  const hasUnsavedChanges = useMemo(() => {
    const chatwootChanged = JSON.stringify(fileIntegrations) !== JSON.stringify(stagedIntegrations);
    const widgetChanged = JSON.stringify(widgetFileIntegrations) !== JSON.stringify(stagedWidgetIntegrations);
    const instagramChanged = JSON.stringify(instagramFileIntegrations) !== JSON.stringify(stagedInstagramIntegrations);
    return chatwootChanged || widgetChanged || instagramChanged;
  }, [fileIntegrations, stagedIntegrations, widgetFileIntegrations, stagedWidgetIntegrations, instagramFileIntegrations, stagedInstagramIntegrations]);

  const handleSaveAllChanges = async () => {
    setSavingIntegrations(true);
    setError(null);

    // Debug logging
    console.log('=== Save All Changes Debug ===');
    console.log('stagedInstagramIntegrations:', stagedInstagramIntegrations);
    console.log('instagramFileIntegrations:', instagramFileIntegrations);
    console.log('stagedWidgetIntegrations:', stagedWidgetIntegrations);
    console.log('widgetFileIntegrations:', widgetFileIntegrations);
    console.log('availableInstagramChannels:', availableInstagramChannels);
    console.log('availableWidgetIntegrations:', availableWidgetIntegrations);

    try {
      let totalChanges = 0;

      // Save Chatwoot integration changes
      const chatwootChangedFileIds = Object.keys(stagedIntegrations).filter(fileId => {
        const original = fileIntegrations[fileId] || [];
        const staged = stagedIntegrations[fileId] || [];
        return JSON.stringify(original.sort()) !== JSON.stringify(staged.sort());
      });

      for (const fileId of chatwootChangedFileIds) {
        const { error: deleteError } = await supabase
          .from('user_file_chatwoot_integrations')
          .delete()
          .eq('user_file_id', parseInt(fileId));

        if (deleteError) throw deleteError;

        const integrationIds = stagedIntegrations[fileId] || [];
        if (integrationIds.length > 0) {
          const mappings = integrationIds.map(intId => ({
            user_file_id: parseInt(fileId),
            chatwoot_configuration_id: intId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }));

          const { error: insertError } = await supabase
            .from('user_file_chatwoot_integrations')
            .insert(mappings);

          if (insertError) throw insertError;
        }
      }
      totalChanges += chatwootChangedFileIds.length;

      // Save Widget integration changes
      const widgetChangedFileIds = Object.keys(stagedWidgetIntegrations).filter(fileId => {
        const original = widgetFileIntegrations[fileId] || [];
        const staged = stagedWidgetIntegrations[fileId] || [];
        return JSON.stringify(original.sort()) !== JSON.stringify(staged.sort());
      });

      for (const fileId of widgetChangedFileIds) {
        const { error: deleteError } = await supabase
          .from('user_file_widget_integrations')
          .delete()
          .eq('user_file_id', parseInt(fileId));

        if (deleteError) throw deleteError;

        const widgetIds = stagedWidgetIntegrations[fileId] || [];
        if (widgetIds.length > 0) {
          const mappings = widgetIds.map(wid => ({
            user_file_id: parseInt(fileId),
            widget_configuration_id: wid
          }));

          const { error: insertError } = await supabase
            .from('user_file_widget_integrations')
            .insert(mappings);

          if (insertError) throw insertError;
        }
      }
      totalChanges += widgetChangedFileIds.length;

      // Save Instagram integration changes
      const instagramChangedFileIds = Object.keys(stagedInstagramIntegrations).filter(fileId => {
        const original = instagramFileIntegrations[fileId] || [];
        const staged = stagedInstagramIntegrations[fileId] || [];
        return JSON.stringify(original.sort()) !== JSON.stringify(staged.sort());
      });

      for (const fileId of instagramChangedFileIds) {
        const { error: deleteError } = await supabase
          .from('user_file_instagram_integrations')
          .delete()
          .eq('user_file_id', parseInt(fileId));

        if (deleteError) throw deleteError;

        const channelIds = stagedInstagramIntegrations[fileId] || [];
        if (channelIds.length > 0) {
          const mappings = channelIds.map(chId => ({
            user_file_id: parseInt(fileId),
            instagram_channel_id: chId
          }));

          const { error: insertError } = await supabase
            .from('user_file_instagram_integrations')
            .insert(mappings);

          if (insertError) throw insertError;
        }
      }
      totalChanges += instagramChangedFileIds.length;

      if (totalChanges === 0) {
        alert('No changes to save.');
        setSavingIntegrations(false);
        return;
      }

      // Update state to reflect saved changes
      setFileIntegrations({ ...stagedIntegrations });
      setWidgetFileIntegrations({ ...stagedWidgetIntegrations });
      setInstagramFileIntegrations({ ...stagedInstagramIntegrations });
      alert(`Successfully updated integrations for ${totalChanges} file(s)!`);

    } catch (err) {
      console.error('Failed to save changes:', err);
      setError(`Failed to save changes: ${err.message}`);
    } finally {
      setSavingIntegrations(false);
    }
  };

  // Discard staged changes
  const handleDiscardChanges = () => {
    setStagedIntegrations({ ...fileIntegrations });
    setStagedWidgetIntegrations({ ...widgetFileIntegrations });
    setStagedInstagramIntegrations({ ...instagramFileIntegrations });
    setSelectedFileIds(new Set());
  };

  // Delete file handler (admin only)
  const handleDeleteFile = async (fileId, fileName) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${fileName}"?\n\nThis will remove:\n- File from user_files\n- All chunks from document_chunks\n- Embeddings from Milvus\n- Storage file\n- All integration mappings\n\nThis action cannot be undone.`)) {
      return;
    }

    setDeletingFileId(fileId);
    try {
      const token = await getAuthToken();
      const backendUrl = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_BACKEND_URL) || process.env.REACT_APP_BACKEND_URL || '';

      const response = await fetch(`${backendUrl}/admin/delete_file_by_id/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to delete file (${response.status})`);
      }

      const result = await response.json();
      console.log('Delete result:', result);

      alert(`File "${fileName}" deleted successfully.\n\nDetails:\n${JSON.stringify(result.details, null, 2)}`);

      // Remove from local state
      setFiles(prev => prev.filter(f => f.id !== fileId));

      // Clear selection if it was the selected file
      if (selectedFile?.id === fileId) {
        setSelectedFile(null);
      }

      // Remove from staged integrations and file integrations
      setStagedIntegrations(prev => {
        const newState = { ...prev };
        delete newState[fileId];
        return newState;
      });

      setFileIntegrations(prev => {
        const newState = { ...prev };
        delete newState[fileId];
        return newState;
      });

      // Remove from selection
      setSelectedFileIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(fileId);
        return newSet;
      });

    } catch (err) {
      console.error('Delete error:', err);
      alert(`Failed to delete file: ${err.message}`);
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setError(null);

    const updates = files
      .filter(file => chatwootSelection[file.id] !== undefined && chatwootSelection[file.id] !== !!file.use_in_chatwoot)
      .map(file => ({
        id: file.id,
        user_id: userId,
        use_in_chatwoot: chatwootSelection[file.id],
      }));

    if (updates.length === 0) {
      alert("No changes to save.");
      setIsSaving(false);
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from('user_files')
        .upsert(updates, { onConflict: 'id' });

      if (updateError) throw updateError;

      alert('Chatwoot settings saved successfully!');
      // Refetch data to ensure UI is in sync with DB
      fetchInitialData();

    } catch (err) {
      setError(`Failed to save changes: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Admin validate selected files
  const handleValidateSelected = async () => {
    if (selectedFileIds.size === 0) {
      alert('Please select at least one file to validate.');
      return;
    }

    setIsValidating(true);
    setValidationResults(null);
    setError(null);

    try {
      const token = await getAuthToken();
      const backendUrl = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_BACKEND_URL) || process.env.REACT_APP_BACKEND_URL || '';

      const fileIds = Array.from(selectedFileIds);

      const response = await fetch(`${backendUrl}/admin/validate_batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          file_ids: fileIds,
          target_user_id: userId  // Admin context - validate for this user
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Validation failed (${response.status})`);
      }

      const result = await response.json();
      setValidationResults(result);

      // Show summary
      const { complete, incomplete, failed, success_rate } = result;
      let msg = `Validation complete: ${complete}/${fileIds.length} files valid (${success_rate}% success rate).`;
      if (incomplete > 0) msg += ` ${incomplete} incomplete.`;
      if (failed > 0) msg += ` ${failed} failed.`;

      if (result.problematic_files && result.problematic_files.length > 0) {
        const problemSummary = result.problematic_files.map(pf =>
          `• ${pf.filename}: ${pf.issues.join(', ')}`
        ).join('\n');
        alert(`${msg}\n\nProblematic files:\n${problemSummary}`);
      } else {
        alert(msg);
      }

    } catch (err) {
      console.error('Validation error:', err);
      setError(`Validation failed: ${err.message}`);
    } finally {
      setIsValidating(false);
    }
  };

  // Admin force recrawl selected files
  const handleForceRecrawlSelected = async () => {
    if (selectedFileIds.size === 0) {
      alert('Please select at least one file to recrawl.');
      return;
    }

    // Filter to only crawled files (those with source_url)
    const crawledFileIds = Array.from(selectedFileIds).filter(fileId => {
      const file = files.find(f => f.id === fileId);
      return file && file.source_url && file.source_url.startsWith('http');
    });

    if (crawledFileIds.length === 0) {
      alert('No crawled pages selected. Force Recrawl only works on web pages, not uploaded files.');
      return;
    }

    if (!window.confirm(`Force recrawl ${crawledFileIds.length} page(s)?\n\nThis will:\n• Delete existing chunks and vectors\n• Re-crawl each URL\n• Re-process and re-embed content\n\nContinue?`)) {
      return;
    }

    setIsForceRecrawling(true);
    setError(null);

    try {
      const token = await getAuthToken();
      const backendUrl = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_BACKEND_URL) || process.env.REACT_APP_BACKEND_URL || '';

      let successCount = 0;
      let failedCount = 0;
      const errors = [];

      for (const fileId of crawledFileIds) {
        try {
          const response = await fetch(`${backendUrl}/admin/force_recrawl/${fileId}?target_user_id=${userId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            successCount++;
          } else {
            const errData = await response.json();
            failedCount++;
            errors.push(`File ${fileId}: ${errData.detail || response.status}`);
          }
        } catch (e) {
          failedCount++;
          errors.push(`File ${fileId}: ${e.message}`);
        }
      }

      let msg = `Force recrawl initiated: ${successCount}/${crawledFileIds.length} successful.`;
      if (failedCount > 0) {
        msg += `\n\nFailed (${failedCount}):\n${errors.join('\n')}`;
      }
      alert(msg);

      // Refresh file list after recrawl
      setTimeout(() => fetchInitialData(), 2000);

    } catch (err) {
      console.error('Force recrawl error:', err);
      setError(`Force recrawl failed: ${err.message}`);
    } finally {
      setIsForceRecrawling(false);
    }
  };

  const isAllSelected = files.length > 0 && files.every(file => chatwootSelection[file.id]);
  const isSomeSelected = files.some(file => chatwootSelection[file.id]) && !isAllSelected;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link component={RouterLink} underline="hover" color="inherit" to="/admin">Admin Panel</Link>
        <Link component={RouterLink} underline="hover" color="inherit" to={`/admin/user/${userId}`}>User Details</Link>
        <Typography color="text.primary">User Files</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>Files for User: {userEmail}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Batch Operations Panel */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Batch File Operations</Typography>

          <Grid container spacing={2}>
            {/* Select Files By */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Select Files By:</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                <Button variant="outlined" size="small" onClick={handleSelectAll}>
                  All Files ({filteredAndSortedFiles.length})
                </Button>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>By Team</InputLabel>
                  <Select
                    value=""
                    label="By Team"
                    onChange={(e) => handleSelectByTeam(e.target.value)}
                  >
                    <MenuItem value=""><em>Select team...</em></MenuItem>
                    {availableTeams.map(teamId => (
                      <MenuItem key={teamId} value={teamId}>
                        {teamNames[teamId] || `Team: ${teamId.substring(0, 8)}...`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>By Status</InputLabel>
                  <Select
                    value=""
                    label="By Status"
                    onChange={(e) => handleSelectByStatus(e.target.value)}
                  >
                    <MenuItem value=""><em>Select status...</em></MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="processing">Processing</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="failed">Failed</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="outlined" size="small" color="secondary" onClick={handleClearSelection}>
                  Clear Selection
                </Button>
                <Chip
                  label={`${selectedFileIds.size} files selected`}
                  color={selectedFileIds.size > 0 ? "primary" : "default"}
                />
                {/* Admin Actions: Validate & Force Recrawl */}
                <Button
                  variant="contained"
                  size="small"
                  color="success"
                  startIcon={isValidating ? <CircularProgress size={16} color="inherit" /> : <VerifiedIcon />}
                  onClick={handleValidateSelected}
                  disabled={selectedFileIds.size === 0 || isValidating}
                >
                  {isValidating ? 'Validating...' : `Validate (${selectedFileIds.size})`}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  color="warning"
                  startIcon={isForceRecrawling ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                  onClick={handleForceRecrawlSelected}
                  disabled={selectedFileIds.size === 0 || isForceRecrawling}
                >
                  {isForceRecrawling ? 'Recrawling...' : `Force Recrawl (${selectedFileIds.size})`}
                </Button>
              </Stack>
            </Grid>

            {/* Apply Integration */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Then Apply Integration:</Typography>
              <Stack direction="row" spacing={1}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Integration</InputLabel>
                  <Select
                    value=""
                    label="Integration"
                    disabled={selectedFileIds.size === 0 || availableIntegrations.length === 0}
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBatchApplyIntegration(e.target.value);
                        const intName = availableIntegrations.find(i => i.integration_id === e.target.value)?.integration_name;
                        alert(`Applied "${intName}" to ${selectedFileIds.size} file(s). Click "Save All Changes" to confirm.`);
                      }
                    }}
                  >
                    <MenuItem value=""><em>Select integration...</em></MenuItem>
                    {availableIntegrations.map(int => (
                      <MenuItem key={int.integration_id} value={int.integration_id}>
                        {int.integration_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Grid>

            {/* Remove Integration */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Or Remove Integration:</Typography>
              <Stack direction="row" spacing={1}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Integration</InputLabel>
                  <Select
                    value=""
                    label="Integration"
                    disabled={selectedFileIds.size === 0 || availableIntegrations.length === 0}
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBatchRemoveIntegration(e.target.value);
                        const intName = availableIntegrations.find(i => i.integration_id === e.target.value)?.integration_name;
                        alert(`Removed "${intName}" from ${selectedFileIds.size} file(s). Click "Save All Changes" to confirm.`);
                      }
                    }}
                  >
                    <MenuItem value=""><em>Select integration...</em></MenuItem>
                    {availableIntegrations.map(int => (
                      <MenuItem key={int.integration_id} value={int.integration_id}>
                        {int.integration_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Grid>

            {/* Widget Integration Section */}
            {availableWidgetIntegrations.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 1, fontWeight: 'bold' }}>
                    🔧 Widget Integrations
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Apply Widget:</Typography>
                  <Stack direction="row" spacing={1}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Widget</InputLabel>
                      <Select
                        value=""
                        label="Widget"
                        disabled={selectedFileIds.size === 0}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleBatchApplyWidgetIntegration(e.target.value);
                            const widgetName = availableWidgetIntegrations.find(w => w.id === e.target.value)?.widget_name;
                            alert(`Applied widget "${widgetName}" to ${selectedFileIds.size} file(s). Click "Save All Changes" to confirm.`);
                          }
                        }}
                      >
                        <MenuItem value=""><em>Select widget...</em></MenuItem>
                        {availableWidgetIntegrations.map(widget => (
                          <MenuItem key={widget.id} value={widget.id}>
                            {widget.widget_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Remove Widget:</Typography>
                  <Stack direction="row" spacing={1}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Widget</InputLabel>
                      <Select
                        value=""
                        label="Widget"
                        disabled={selectedFileIds.size === 0}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleBatchRemoveWidgetIntegration(e.target.value);
                            const widgetName = availableWidgetIntegrations.find(w => w.id === e.target.value)?.widget_name;
                            alert(`Removed widget "${widgetName}" from ${selectedFileIds.size} file(s). Click "Save All Changes" to confirm.`);
                          }
                        }}
                      >
                        <MenuItem value=""><em>Select widget...</em></MenuItem>
                        {availableWidgetIntegrations.map(widget => (
                          <MenuItem key={widget.id} value={widget.id}>
                            {widget.widget_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>
              </>
            )}

            {/* Instagram Integration Section */}
            {availableInstagramChannels.length > 0 && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ mt: 1, fontWeight: 'bold' }}>
                    📸 Instagram Channels
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Apply Instagram Channel:</Typography>
                  <Stack direction="row" spacing={1}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Instagram</InputLabel>
                      <Select
                        value=""
                        label="Instagram"
                        disabled={selectedFileIds.size === 0}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleBatchApplyInstagramIntegration(e.target.value);
                            const channelName = availableInstagramChannels.find(ch => ch.id === e.target.value)?.username || 'channel';
                            alert(`Applied Instagram "@${channelName}" to ${selectedFileIds.size} file(s). Click "Save All Changes" to confirm.`);
                          }
                        }}
                      >
                        <MenuItem value=""><em>Select channel...</em></MenuItem>
                        {availableInstagramChannels.map(channel => (
                          <MenuItem key={channel.id} value={channel.id}>
                            @{channel.username || channel.instagram_id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>Remove Instagram Channel:</Typography>
                  <Stack direction="row" spacing={1}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Instagram</InputLabel>
                      <Select
                        value=""
                        label="Instagram"
                        disabled={selectedFileIds.size === 0}
                        onChange={(e) => {
                          if (e.target.value) {
                            handleBatchRemoveInstagramIntegration(e.target.value);
                            const channelName = availableInstagramChannels.find(ch => ch.id === e.target.value)?.username || 'channel';
                            alert(`Removed Instagram "@${channelName}" from ${selectedFileIds.size} file(s). Click "Save All Changes" to confirm.`);
                          }
                        }}
                      >
                        <MenuItem value=""><em>Select channel...</em></MenuItem>
                        {availableInstagramChannels.map(channel => (
                          <MenuItem key={channel.id} value={channel.id}>
                            @{channel.username || channel.instagram_id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Grid>
              </>
            )}

            {/* Save/Discard Actions */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                {hasUnsavedChanges && (
                  <Alert severity="warning" sx={{ flexGrow: 1 }}>
                    You have unsaved changes. Click "Save All Changes" to apply them.
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  onClick={handleDiscardChanges}
                  disabled={!hasUnsavedChanges || savingIntegrations}
                >
                  Discard Changes
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveAllChanges}
                  disabled={!hasUnsavedChanges || savingIntegrations}
                >
                  {savingIntegrations ? <CircularProgress size={20} /> : 'Save All Changes'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" component="div">File List</Typography>
            <Button variant="outlined" size="small" onClick={handleSaveChanges} disabled={isSaving || loadingFiles}>
              {isSaving ? <CircularProgress size={20} /> : 'Save Legacy'}
            </Button>
          </Box>

          {/* Sorting and Pagination Controls */}
          <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <MenuItem value="date_desc">Newest First</MenuItem>
                <MenuItem value="date_asc">Oldest First</MenuItem>
                <MenuItem value="name_asc">Name (A-Z)</MenuItem>
                <MenuItem value="name_desc">Name (Z-A)</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Show</InputLabel>
              <Select
                value={itemsPerPage}
                label="Show"
                onChange={(e) => {
                  setItemsPerPage(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={500}>500</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredAndSortedFiles.length)} - {Math.min(currentPage * itemsPerPage, filteredAndSortedFiles.length)} of {filteredAndSortedFiles.length} files
          </Typography>

          {/* NEW: Show integration info */}
          {loadingIntegrations ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1, pb: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Loading integrations...</Typography>
            </Box>
          ) : availableIntegrations.length > 0 ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="caption">
                {availableIntegrations.length} active integration{availableIntegrations.length !== 1 ? 's' : ''} available.
                Click each file to map to specific integrations below.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="caption">
                No Chatwoot integrations found. Create one in Dashboard → Integrations → Chatwoot Integrations.
              </Typography>
            </Alert>
          )}

          <FormControlLabel
            control={<Checkbox
              indeterminate={isSomeSelected}
              checked={isAllSelected}
              onChange={handleSelectAllChatwoot}
              disabled={files.length === 0}
            />}
            label="Select All (Legacy)"
            sx={{ pl: 1, pb: 1 }}
          />
          <Paper sx={{ height: '60vh', overflow: 'auto' }}>
            {loadingFiles ? <CircularProgress sx={{ m: 2 }} /> : (
              <List sx={{ p: 0 }}>
                {paginatedFiles.map(file => (
                  <ListItem
                    key={file.id}
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {file.source_url && (
                          <Tooltip title={`Source: ${file.source_url}`}>
                            <IconButton edge="end" aria-label="source link" onClick={(e) => { e.stopPropagation(); window.open(file.source_url, '_blank', 'noopener,noreferrer'); }}>
                              <LinkIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete file permanently">
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={(e) => { e.stopPropagation(); handleDeleteFile(file.id, file.file_name); }}
                            disabled={deletingFileId === file.id}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                    disablePadding
                    sx={{ flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    <ListItemButton
                      selected={selectedFile?.id === file.id}
                      onClick={() => handleFileSelect(file)}
                    >
                      <Checkbox
                        edge="start"
                        checked={selectedFileIds.has(file.id)}
                        tabIndex={-1}
                        disableRipple
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFileSelection(file.id);
                        }}
                        icon={<CheckBoxOutlineBlankIcon />}
                        checkedIcon={<CheckBoxIcon />}
                        title="Select for batch operations"
                      />
                      <ListItemText
                        primary={file.file_name}
                        secondary={
                          <React.Fragment>
                            Status: {file.processing_status || file.status}
                            {(() => {
                              const chatwootCount = (stagedIntegrations[file.id] || []).length;
                              const widgetCount = (stagedWidgetIntegrations[file.id] || []).length;
                              const instagramCount = (stagedInstagramIntegrations[file.id] || []).length;
                              const totalCount = chatwootCount + widgetCount + instagramCount;

                              const hasChanges =
                                JSON.stringify(fileIntegrations[file.id] || []) !== JSON.stringify(stagedIntegrations[file.id] || []) ||
                                JSON.stringify(widgetFileIntegrations[file.id] || []) !== JSON.stringify(stagedWidgetIntegrations[file.id] || []) ||
                                JSON.stringify(instagramFileIntegrations[file.id] || []) !== JSON.stringify(stagedInstagramIntegrations[file.id] || []);

                              if (totalCount === 0) return null;

                              return (
                                <span style={{ marginLeft: 8, color: '#1976d2' }}>
                                  • {totalCount} channel{totalCount !== 1 ? 's' : ''}
                                  {hasChanges && ' (*)'}
                                </span>
                              );
                            })()}
                          </React.Fragment>
                        }
                      />
                    </ListItemButton>

                    {/* Unified Multi-Channel Integration selector per file */}
                    {(availableIntegrations.length > 0 || availableWidgetIntegrations.length > 0 || availableInstagramChannels.length > 0) && (
                      <Box sx={{ px: 2, pb: 1, pt: 0.5 }}>
                        <FormControl fullWidth size="small" onClick={(e) => e.stopPropagation()}>
                          <InputLabel sx={{ fontSize: '0.75rem' }}>Integrations</InputLabel>
                          <Select
                            multiple
                            value={[
                              ...(stagedIntegrations[file.id] || []).map(id => `chatwoot:${id}`),
                              ...(stagedWidgetIntegrations[file.id] || []).map(id => `widget:${id}`),
                              ...(stagedInstagramIntegrations[file.id] || []).map(id => `instagram:${id}`)
                            ]}
                            onChange={(e) => {
                              const selected = e.target.value;
                              // Parse selected values and distribute to correct state
                              const chatwootIds = selected.filter(v => v.startsWith('chatwoot:')).map(v => v.replace('chatwoot:', ''));
                              const widgetIds = selected.filter(v => v.startsWith('widget:')).map(v => v.replace('widget:', ''));
                              const instagramIds = selected.filter(v => v.startsWith('instagram:')).map(v => v.replace('instagram:', ''));

                              handleIndividualIntegrationChange(file.id, chatwootIds);
                              handleIndividualWidgetIntegrationChange(file.id, widgetIds);
                              handleIndividualInstagramIntegrationChange(file.id, instagramIds);
                            }}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.length === 0 ? (
                                  <Typography variant="caption" color="text.secondary">None</Typography>
                                ) : (
                                  selected.map((val) => {
                                    let label = 'Unknown';
                                    let icon = '';
                                    if (val.startsWith('chatwoot:')) {
                                      const id = val.replace('chatwoot:', '');
                                      const int = availableIntegrations.find(i => i.integration_id === id);
                                      label = int?.integration_name || 'Unknown';
                                      icon = '💬 ';
                                    } else if (val.startsWith('widget:')) {
                                      const id = val.replace('widget:', '');
                                      const widget = availableWidgetIntegrations.find(w => w.id === id);
                                      label = widget?.widget_name || 'Unknown';
                                      icon = '🌐 ';
                                    } else if (val.startsWith('instagram:')) {
                                      const id = val.replace('instagram:', '');
                                      const channel = availableInstagramChannels.find(ch => ch.id === id);
                                      label = '@' + (channel?.username || channel?.instagram_id || 'Unknown');
                                      icon = '📸 ';
                                    }
                                    return (
                                      <Chip
                                        key={val}
                                        label={icon + label}
                                        size="small"
                                      />
                                    );
                                  })
                                )}
                              </Box>
                            )}
                            displayEmpty
                            sx={{ fontSize: '0.875rem' }}
                          >
                            <MenuItem disabled value="">
                              <em>Select integrations...</em>
                            </MenuItem>

                            {/* Chatwoot Integrations Group */}
                            {availableIntegrations.length > 0 && (
                              <MenuItem disabled sx={{ bgcolor: 'action.hover', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                💬 Chatwoot Integrations
                              </MenuItem>
                            )}
                            {availableIntegrations.map(int => (
                              <MenuItem key={`chatwoot:${int.integration_id}`} value={`chatwoot:${int.integration_id}`} sx={{ pl: 3 }}>
                                <Checkbox
                                  checked={(stagedIntegrations[file.id] || []).includes(int.integration_id)}
                                  size="small"
                                />
                                <ListItemText
                                  primary={int.integration_name}
                                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                                />
                              </MenuItem>
                            ))}

                            {/* Widget Integrations Group */}
                            {availableWidgetIntegrations.length > 0 && (
                              <MenuItem disabled sx={{ bgcolor: 'action.hover', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                🌐 Widget Integrations
                              </MenuItem>
                            )}
                            {availableWidgetIntegrations.map(widget => (
                              <MenuItem key={`widget:${widget.id}`} value={`widget:${widget.id}`} sx={{ pl: 3 }}>
                                <Checkbox
                                  checked={(stagedWidgetIntegrations[file.id] || []).includes(widget.id)}
                                  size="small"
                                />
                                <ListItemText
                                  primary={widget.widget_name}
                                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                                />
                              </MenuItem>
                            ))}

                            {/* Instagram Channels Group */}
                            {availableInstagramChannels.length > 0 && (
                              <MenuItem disabled sx={{ bgcolor: 'action.hover', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                📸 Instagram Channels
                              </MenuItem>
                            )}
                            {availableInstagramChannels.map(channel => (
                              <MenuItem key={`instagram:${channel.id}`} value={`instagram:${channel.id}`} sx={{ pl: 3 }}>
                                <Checkbox
                                  checked={(stagedInstagramIntegrations[file.id] || []).includes(channel.id)}
                                  size="small"
                                />
                                <ListItemText
                                  primary={`@${channel.username || channel.instagram_id}`}
                                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, page) => setCurrentPage(page)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" gutterBottom>
            File Content: {selectedFile ? selectedFile.file_name : 'No file selected'}
          </Typography>

          {/* NEW: Searchable Summary Box */}
          {selectedFile && !loadingContent && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                Searchable Summary (for RAG):
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: '150px', overflow: 'auto' }}>
                {selectedFile.searchable_summary || <em style={{ color: '#999' }}>No searchable summary available</em>}
              </Typography>
            </Paper>
          )}

          {/* NEW: Summary Box */}
          {selectedFile && !loadingContent && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#fff3e0' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="warning.dark" gutterBottom>
                Summary (general):
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: '150px', overflow: 'auto' }}>
                {selectedFile.summary || <em style={{ color: '#999' }}>No summary available</em>}
              </Typography>
            </Paper>
          )}

          <Paper sx={{ p: 2, height: '60vh', overflow: 'auto' }}>
            {selectedFile ? (
              loadingContent ? <CircularProgress /> : (
                <TextField
                  multiline
                  fullWidth
                  rows={22}
                  value={selectedFile.content || ''}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': { height: '100%', alignItems: 'flex-start' },
                    '& .MuiOutlinedInput-input': { height: '100% !important', overflow: 'auto !important' }
                  }}
                />
              )
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="textSecondary">Select a file from the left to view its content.</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default UserFilesPage; 